import React, { useState, useEffect } from 'react'
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaClock,
  FaWhatsapp,
} from 'react-icons/fa'
import contactService from '../services/contactService'
import settingsService from '../services/settingsService'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import SEO from '../utils/SEO'
import { trackContactSubmit } from '../utils/analytics'
import Button from '../components/ui/Button'
import { useAuth } from '../context/AuthContext'

const CONTACT_NAME_MAX_LENGTH = 255
const CONTACT_EMAIL_MAX_LENGTH = 255
const CONTACT_MOBILE_MAX_LENGTH = 20
const CONTACT_SUBJECT_MAX_LENGTH = 255

const Contact = () => {
  const { user } = useAuth()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile_number: '',
    subject: '',
    message: '',
  })
  const [loading, setLoading] = useState(false)
  const [settingsLoading, setSettingsLoading] = useState(true)
  const [settings, setSettings] = useState({
    contact_address: '123 Travel Street, Adventure City, IN 110001',
    contact_phone: '+91 99999 99999',
    contact_phone_secondary: '',
    contact_email: 'info@himachalyatra.com',
    contact_email_support: 'support@himachalyatra.com',
    contact_timings: 'Monday - Saturday: 9:00 AM - 6:00 PM',
    contact_emergency_phone: '',
    office_location_name: 'Head Office',
    social_whatsapp: '',
  })

  useEffect(() => {
    loadSettings()
  }, [])

  // Pre-fill form fields from authenticated user's profile
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: prev.name || user.name || '',
        email: prev.email || user.email || '',
        mobile_number: prev.mobile_number || user.mobile || '',
      }))
    }
  }, [user])

  const loadSettings = async () => {
    try {
      const response = await settingsService.getPublicSettings('contact')
      if (response.success && response.settings) {
        setSettings((prev) => ({ ...prev, ...response.settings }))
      }
    } catch (error) {
      console.error('Error loading contact settings:', error)
      // Continue with default settings
    } finally {
      setSettingsLoading(false)
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      await contactService.submitQuery(formData)
      trackContactSubmit()
      
      toast.success("Your query has been submitted! We'll get back to you soon.")
      // Fire Google Ads conversion on successful contact form submission
      if (typeof window.gtag_report_conversion === 'function') {
        window.gtag_report_conversion()
      }
      // Reset form
      setFormData({
        name: '',
        email: '',
        mobile_number: '',
        subject: '',
        message: '',
      })
    } catch (error) {
      console.error('Submit query error:', error)
      toast.error(error.response?.data?.message || 'Failed to submit query. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEO
        title="Contact Himachal Yatra - Get in Touch"
        description="Contact Himachal Yatra for tour inquiries, bookings, and support. Call +91 7418100009 or email info@himachalyatra.com. We respond within 24-48 hours."
        canonical="/contact"
        keywords="contact Himachal Yatra, book tour Himachal Pradesh, travel inquiry India"
      />
      <ToastContainer />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">
            Get In Touch
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Have questions about our tours? We're here to help! Send us a message and our team will
            respond within 24-48 hours.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="label">Your Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      maxLength={CONTACT_NAME_MAX_LENGTH}
                      className="input-field"
                      placeholder="John Doe"
                      required
                    />
                  </div>

                  <div>
                    <label className="label">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      maxLength={CONTACT_EMAIL_MAX_LENGTH}
                      className="input-field"
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="label">Mobile Number (Optional)</label>
                    <input
                      type="tel"
                      name="mobile_number"
                      value={formData.mobile_number}
                      onChange={handleChange}
                      maxLength={CONTACT_MOBILE_MAX_LENGTH}
                      className="input-field"
                      placeholder="+91 9999999999"
                    />
                  </div>

                  <div>
                    <label className="label">Subject *</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      maxLength={CONTACT_SUBJECT_MAX_LENGTH}
                      className="input-field"
                      placeholder="Tour Inquiry"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Your Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="input-field"
                    rows="6"
                    placeholder="Tell us about your travel plans or questions..."
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  loading={loading}
                  icon={loading ? undefined : <FaPaperPlane />}
                  fullWidth
                  className="md:w-auto"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            {settingsLoading ? (
              /* Skeleton placeholders while settings load */
              <>
                <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6">
                  <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-4" />
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-start">
                        <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse flex-shrink-0" />
                        <div className="ml-4 flex-1">
                          <div className="h-3 w-12 bg-gray-200 rounded animate-pulse mb-2" />
                          <div className="h-4 w-36 bg-gray-200 rounded animate-pulse" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6">
                  <div className="h-6 w-36 bg-gray-200 rounded animate-pulse mb-4" />
                  <div className="h-4 w-56 bg-gray-200 rounded animate-pulse" />
                </div>
              </>
            ) : (
              <>
                {/* Contact Details Card */}
                <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>

                  <div className="space-y-4">
                    {settings.contact_email && (
                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <FaEnvelope className="text-primary-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm text-gray-500">Email</p>
                          <a
                            href={`mailto:${settings.contact_email}`}
                            className="font-semibold text-gray-900 hover:text-primary-600"
                          >
                            {settings.contact_email}
                          </a>
                          {settings.contact_email_support && (
                            <p className="text-sm text-gray-600 mt-1">
                              Support:{' '}
                              <a
                                href={`mailto:${settings.contact_email_support}`}
                                className="hover:text-primary-600"
                              >
                                {settings.contact_email_support}
                              </a>
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {settings.contact_phone && (
                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <FaPhone className="text-primary-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm text-gray-500">Phone</p>
                          <a
                            href={`tel:${settings.contact_phone}`}
                            className="font-semibold text-gray-900 hover:text-primary-600"
                          >
                            {settings.contact_phone}
                          </a>
                          {settings.contact_phone_secondary && (
                            <p className="text-sm text-gray-600 mt-1">
                              Alt:{' '}
                              <a
                                href={`tel:${settings.contact_phone_secondary}`}
                                className="hover:text-primary-600"
                              >
                                {settings.contact_phone_secondary}
                              </a>
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {settings.social_whatsapp && (
                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <FaWhatsapp className="text-green-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm text-gray-500">WhatsApp</p>
                          <a
                            href={`https://wa.me/${settings.social_whatsapp.replace(/[^0-9]/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-gray-900 hover:text-green-600"
                          >
                            {settings.social_whatsapp}
                          </a>
                        </div>
                      </div>
                    )}

                    {settings.contact_address && (
                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <FaMapMarkerAlt className="text-primary-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm text-gray-500">
                            {settings.office_location_name || 'Address'}
                          </p>
                          <p className="font-semibold text-gray-900 whitespace-pre-line">
                            {settings.contact_address}
                          </p>
                        </div>
                      </div>
                    )}

                    {settings.contact_emergency_phone && (
                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <FaPhone className="text-red-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm text-gray-500">Emergency</p>
                          <a
                            href={`tel:${settings.contact_emergency_phone}`}
                            className="font-semibold text-red-600 hover:text-red-700"
                          >
                            {settings.contact_emergency_phone}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Business Hours Card */}
                {settings.contact_timings && (
                  <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                      <FaClock className="mr-2 text-primary-600" />
                      Business Hours
                    </h3>

                    <div className="space-y-2">
                      <p className="text-gray-700 whitespace-pre-line">
                        {settings.contact_timings}
                      </p>
                    </div>
                  </div>
                )}

                {/* Quick Response Card */}
                <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6 bg-gradient-to-br from-primary-50/80 to-secondary-50/80">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Quick Response</h3>
                  <p className="text-sm text-gray-600">
                    We typically respond to all queries within 24-48 hours. For urgent matters,
                    please call us directly.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
            Frequently Asked Questions
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">How do I book a tour?</h3>
              <p className="text-gray-600">
                Browse our tours, select your preferred package, and click "Reserve Your Spot". You
                can customize your booking with additional services and activities. Free
                cancellation up to 48 hours.
              </p>
            </div>

            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                What payment methods do you accept?
              </h3>
              <p className="text-gray-600">
                We accept all major credit cards, debit cards, UPI, and net banking. Payment is
                secure and encrypted.
              </p>
            </div>

            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Can I cancel or modify my booking?
              </h3>
              <p className="text-gray-600">
                Yes, cancellations and modifications are allowed based on our cancellation policy.
                Contact us for assistance.
              </p>
            </div>

            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Do you offer group discounts?
              </h3>
              <p className="text-gray-600">
                Yes! We offer special discounts for group bookings. Use code "GROUP20" for groups of
                10 or more people.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Contact
